import HyperDX from '@hyperdx/browser';
import { ErrorBoundary } from 'react-error-boundary';
import { getEmail, getRealm, getUserPermissions } from 'utils/storage';

function readEnv(value) {
  if (value == null) return '';
  return String(value).trim().replace(/^['"]|['"]$/g, '');
}

const COLLECTOR_URL = readEnv(import.meta.env.VITE_CLICKSTACK_OTLP_URL || import.meta.env.VITE_HYPERDX_URL);
const API_KEY = readEnv(import.meta.env.VITE_CLICKSTACK_INGESTION_KEY || import.meta.env.VITE_HYPERDX_API_KEY);
const SERVICE =
  readEnv(import.meta.env.VITE_CLICKSTACK_SERVICE || import.meta.env.VITE_OTEL_SERVICE_NAME || import.meta.env.VITE_HYPERDX_SERVICE) ||
  'xpay-admin-dashboard';
const ENVIRONMENT = readEnv(import.meta.env.VITE_HYPERDX_ENV) || import.meta.env.MODE;
const CLICKSTACK_FLAG = readEnv(import.meta.env.VITE_CLICKSTACK_ENABLED);
const REQUIRE_KEY = readEnv(import.meta.env.VITE_CLICKSTACK_REQUIRE_INGESTION_KEY) === 'true';
const REPLAY_ENABLED = readEnv(import.meta.env.VITE_CLICKSTACK_REPLAY_ENABLED) !== 'false';
const SEND_LOGS_ON_LOCAL = readEnv(import.meta.env.VITE_CLICKSTACK_SEND_LOGS_ON_LOCAL) === 'true';
const isLocal = import.meta.env.DEV;
const LOCAL_OTLP_PROXY = readEnv(import.meta.env.VITE_CLICKSTACK_OTLP_PROXY) || '/otel';

// In local dev, route through Vite proxy to avoid cross-origin CORS on the collector.
const EFFECTIVE_COLLECTOR_URL = isLocal && SEND_LOGS_ON_LOCAL ? LOCAL_OTLP_PROXY : COLLECTOR_URL;

const isEnabled =
  Boolean(COLLECTOR_URL) &&
  CLICKSTACK_FLAG !== 'false' &&
  (!REQUIRE_KEY || Boolean(API_KEY)) &&
  (!isLocal || SEND_LOGS_ON_LOCAL);

function escapeRegex(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function parseTracePropagationTargets() {
  const raw = import.meta.env.VITE_HYPERDX_TRACE_TARGETS;
  if (!raw) return [];
  return String(raw)
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((host) => new RegExp(escapeRegex(host), 'i'));
}

function readDisplayName() {
  const raw = window.localStorage.getItem('name');
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === 'string' ? parsed : raw;
  } catch {
    return raw;
  }
}

function compactAttributes(attributes) {
  return Object.fromEntries(Object.entries(attributes).filter(([, value]) => value != null && value !== ''));
}

export function identifyUser({ storeId } = {}) {
  if (!isEnabled) return;
  let permissions;
  try {
    permissions = getUserPermissions();
  } catch {
    permissions = null;
  }
  HyperDX.setGlobalAttributes(
    compactAttributes({
      userId: String(getRealm() || getEmail() || ''),
      userEmail: getEmail() || undefined,
      userName: readDisplayName() || undefined,
      teamName: getRealm() ? String(getRealm()) : undefined,
      storeId: storeId ? String(storeId) : undefined,
      roles: Array.isArray(permissions) ? permissions.join(',') : permissions ? String(permissions) : undefined,
    }),
  );
}

function initHyperDX() {
  if (!isEnabled) return;
  try {
    HyperDX.init({
      url: EFFECTIVE_COLLECTOR_URL,
      apiKey: API_KEY || '',
      service: SERVICE,
      tracePropagationTargets: parseTracePropagationTargets(),
      consoleCapture: true,
      advancedNetworkCapture: true,
      maskAllInputs: true,
      disableReplay: !REPLAY_ENABLED,
      otelResourceAttributes: {
        'deployment.environment': ENVIRONMENT,
      },
    });
    HyperDX.attachToReactErrorBoundary(ErrorBoundary);
  } catch (err) {
    console.error('HyperDX failed to initialize', err);
  }
}

initHyperDX();
